import { useMemo } from "react";
import { Eye } from "lucide-react";
import type { SubstrateProfile } from "../types/substrate";

interface SubstrateVisualizationProps {
  substrate: SubstrateProfile;
  color: string;
  size?: "small" | "medium" | "large";
  showTexture?: boolean;
  className?: string;
}

export function SubstrateVisualization({
  substrate,
  color,
  size = "medium",
  showTexture = true,
  className = "",
}: SubstrateVisualizationProps) {
  const textureStyle = useMemo(() => {
    if (!showTexture) return {};

    const baseOpacity = substrate.brightness / 100;
    const glossIntensity = substrate.glossiness / 100;

    // Create texture patterns based on substrate category and properties
    const textures = {
      paper: {
        background: `
          radial-gradient(circle at 20% 50%, rgba(255,255,255,${0.1 * glossIntensity}) 0%, transparent 50%),
          radial-gradient(circle at 80% 80%, rgba(0,0,0,${0.05 * (1 - baseOpacity)}) 0%, transparent 50%),
          linear-gradient(45deg, 
            rgba(255,255,255,${0.03 * glossIntensity}) 25%, 
            transparent 25%, 
            transparent 75%, 
            rgba(255,255,255,${0.03 * glossIntensity}) 75%
          ),
          linear-gradient(-45deg, 
            rgba(255,255,255,${0.03 * glossIntensity}) 25%, 
            transparent 25%, 
            transparent 75%, 
            rgba(255,255,255,${0.03 * glossIntensity}) 75%
          )
        `,
        backgroundSize: "100% 100%, 100% 100%, 4px 4px, 4px 4px",
        backgroundPosition: "0% 0%, 0% 0%, 0% 0%, 2px 2px",
        mixBlendMode: glossIntensity > 0.7 ? "screen" : "normal",
      },
      metallic: {
        background: `
          linear-gradient(135deg, 
            rgba(255,255,255,${0.3 * glossIntensity}) 0%, 
            transparent 30%, 
            rgba(0,0,0,${0.1 * (1 - baseOpacity)}) 70%, 
            rgba(255,255,255,${0.2 * glossIntensity}) 100%
          ),
          repeating-linear-gradient(
            90deg,
            transparent,
            transparent 2px,
            rgba(255,255,255,${0.1 * glossIntensity}) 2px,
            rgba(255,255,255,${0.1 * glossIntensity}) 4px
          ),
          repeating-linear-gradient(
            0deg,
            transparent,
            transparent 2px,
            rgba(255,255,255,${0.05 * glossIntensity}) 2px,
            rgba(255,255,255,${0.05 * glossIntensity}) 4px
          )
        `,
        backgroundSize: "100% 100%, 10px 10px, 10px 10px",
        mixBlendMode: "screen",
      },
      plastic: {
        background: `
          radial-gradient(ellipse at top, 
            rgba(255,255,255,${0.2 * glossIntensity}) 0%, 
            transparent 60%
          ),
          radial-gradient(ellipse at bottom, 
            rgba(0,0,0,${0.1 * (1 - baseOpacity)}) 0%, 
            transparent 60%
          ),
          linear-gradient(45deg, 
            transparent 40%, 
            rgba(255,255,255,${0.05 * glossIntensity}) 50%, 
            transparent 60%
          )
        `,
        backgroundSize: "100% 100%, 100% 100%, 100% 100%",
        mixBlendMode: glossIntensity > 0.6 ? "soft-light" : "normal",
      },
      fabric: {
        background: `
          repeating-linear-gradient(
            90deg,
            transparent,
            transparent 1px,
            rgba(0,0,0,${0.1 * (1 - baseOpacity)}) 1px,
            rgba(0,0,0,${0.1 * (1 - baseOpacity)}) 2px
          ),
          repeating-linear-gradient(
            0deg,
            transparent,
            transparent 1px,
            rgba(0,0,0,${0.08 * (1 - baseOpacity)}) 1px,
            rgba(0,0,0,${0.08 * (1 - baseOpacity)}) 2px
          ),
          radial-gradient(circle at 30% 40%, 
            rgba(255,255,255,${0.1 * glossIntensity}) 0%, 
            transparent 40%
          )
        `,
        backgroundSize: "100% 100%, 100% 100%, 100% 100%",
        mixBlendMode: "multiply",
      },
    };

    return (
      textures[substrate.category as keyof typeof textures] || textures.paper
    );
  }, [substrate, showTexture]);

  const sizeClasses = {
    small: "w-16 h-16",
    medium: "w-24 h-24",
    large: "w-32 h-32",
  };

  return (
    <div className={`relative ${className}`}>
      {/* Main color with texture */}
      <div
        className={`
          ${sizeClasses[size]} 
          rounded-lg border-2 border-gray-300 
          shadow-sm transition-all duration-200
          hover:shadow-md hover:border-gray-400
          relative overflow-hidden
        `}
        style={{
          backgroundColor: color,
          ...textureStyle,
        }}
      >
        {/* Glossiness overlay */}
        {substrate.glossiness > 50 && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `radial-gradient(circle at 30% 30%, 
                rgba(255,255,255,${substrate.glossiness / 200}) 0%, 
                transparent 70%)`,
            }}
          />
        )}

        {/* Substrate base color tint */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundColor: substrate.baseColor.hex,
            opacity: (100 - substrate.brightness) / 200,
          }}
        />
      </div>

      {/* Zoom indicator */}
      <div className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-md border border-gray-200">
        <Eye className="w-3 h-3 text-gray-500" />
      </div>
    </div>
  );
}

interface SubstrateZoomPreviewProps {
  substrate: SubstrateProfile;
  color: string;
  onClose: () => void;
}

export function SubstrateZoomPreview({
  substrate,
  color,
  onClose,
}: SubstrateZoomPreviewProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                {substrate.name} - Detailed Preview
              </h3>
              <p className="text-gray-600">{substrate.manufacturer}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              ×
            </button>
          </div>
        </div>

        {/* Large Preview */}
        <div className="p-6">
          <div className="flex justify-center mb-6">
            <SubstrateVisualization
              substrate={substrate}
              color={color}
              size="large"
              showTexture={true}
            />
          </div>

          {/* Color Information */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Input Color</h4>
              <div className="flex items-center space-x-3">
                <div
                  className="w-12 h-12 rounded border-2 border-gray-300"
                  style={{ backgroundColor: color }}
                />
                <div>
                  <p className="font-mono text-sm text-gray-700">{color}</p>
                  <p className="text-xs text-gray-500">Original input</p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 mb-3">Substrate Base</h4>
              <div className="flex items-center space-x-3">
                <div
                  className="w-12 h-12 rounded border-2 border-gray-300"
                  style={{ backgroundColor: substrate.baseColor.hex }}
                />
                <div>
                  <p className="font-mono text-sm text-gray-700">
                    {substrate.baseColor.hex}
                  </p>
                  <p className="text-xs text-gray-500">Base color</p>
                </div>
              </div>
            </div>
          </div>

          {/* Substrate Properties */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">
              Substrate Properties
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Category:</span>
                <span className="font-medium capitalize">
                  {substrate.category}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Brightness:</span>
                <span className="font-medium">{substrate.brightness}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Glossiness:</span>
                <span className="font-medium">{substrate.glossiness}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">TAC Limit:</span>
                <span className="font-medium">{substrate.tacLimit}%</span>
              </div>
            </div>

            {/* Absorption Coefficients */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h5 className="text-sm font-medium text-gray-900 mb-2">
                Absorption Coefficients
              </h5>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-600">Cyan:</span>
                  <span className="font-medium">
                    {substrate.absorptionCoefficients.cyan}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Magenta:</span>
                  <span className="font-medium">
                    {substrate.absorptionCoefficients.magenta}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Yellow:</span>
                  <span className="font-medium">
                    {substrate.absorptionCoefficients.yellow}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Black:</span>
                  <span className="font-medium">
                    {substrate.absorptionCoefficients.black}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
